import React, { useEffect, useState } from "react";
import {
  Button,
  TextField,
  Typography,
  Container,
  Grid2,
  CircularProgress,
} from "@mui/material";
import { db } from "../../../firestore-DB-config/firebase-config"; // Ensure to initialize Firestore
import { collection, doc, getDoc, setDoc } from "firebase/firestore";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";

// Define types for the sections
interface Section {
  heading: string;
  paragraph: string;
}

// Function to convert <p> tags back to newlines for display in the form
const convertHtmlToPlainText = (htmlText: string): string => {
  return htmlText
    .replace(/<\/p><p>/g, "\n") // Replace </p><p> with a newline
    .replace(/<\/?p>/g, ""); // Remove any remaining <p> or </p> tags
};

const TermsAndConditionsForm: React.FC = () => {
  const [sections, setSections] = useState<Section[]>([
    { heading: "", paragraph: "" },
  ]);
  const [loading, setLoading] = useState(false); // Loading state

  useEffect(() => {
    // Fetch existing terms and conditions from Firestore
    const fetchTermsAndConditions = async () => {
      const docRef = doc(collection(db, "termsAndConditions"), "latest");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.terms) {
          const formattedSections = data.terms.map((section: Section) => ({
            ...section,
            paragraph: section.paragraph
              ? convertHtmlToPlainText(section.paragraph) // Convert back to plain text
              : section.paragraph,
          }));
          setSections(formattedSections); // Set the existing terms and conditions
        }
      }
    };

    fetchTermsAndConditions();
  }, []);

  // Handle heading input change
  const handleHeadingChange = (index: number, value: string) => {
    const newSections = [...sections];
    newSections[index].heading = value; // Update the heading of the specific section
    setSections(newSections);
  };

  // Handle paragraph input change
  const handleParagraphChange = (index: number, value: string) => {
    const newSections = [...sections];
    newSections[index].paragraph = value; // Update the paragraph of the specific section
    setSections(newSections);
  };

  // Add a new section
  const addSection = () => {
    setSections([...sections, { heading: "", paragraph: "" }]);
  };

  // Remove a section
  const removeSection = (index: number) => {
    const newSections = sections.filter((_, i) => i !== index);
    setSections(newSections);
  };

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true); // Start loading

    try {
      // Format only the paragraph content with <p> tags
      const formattedSections = sections.map((section) => {
        if (section.paragraph) {
          return {
            ...section,
            paragraph: section.paragraph
              .split("\n")
              .map((para) => `<p>${para}</p>`)
              .join(""), // Wrap each paragraph in <p> tags and join them
          };
        }
        return section; 
      });

      // Save the sections to Firestore
      await setDoc(doc(collection(db, "termsAndConditions"), "latest"), {
        terms: formattedSections,
      });
    } catch (error) {
      console.error("Error saving terms and conditions:", error);
    } finally {
      setLoading(false); // End loading
    }
  };

  return (
    <>
      <Header />
      <Container sx={{ my: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, textAlign: "center" }} 
        >
          Add Terms and Conditions
        </Typography>
        <form onSubmit={handleSubmit}>
          {sections.map((section, index) => (
            <Grid2
              container
              spacing={2}
              key={index}
              sx={{ mb: { xs: 2, md: 3 } }} 
            >
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Heading"
                  variant="outlined"
                  fullWidth
                  value={section.heading}
                  onChange={(e) => handleHeadingChange(index, e.target.value)}
                  margin="normal"
                  InputProps={{
                    sx: { borderRadius: "12px" }, 
                  }}
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  label="Paragraph"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={5}
                  value={section.paragraph}
                  onChange={(e) => handleParagraphChange(index, e.target.value)}
                  margin="normal"
                  InputProps={{
                    sx: { borderRadius: "12px" }, 
                  }}
                />
              </Grid2>
              <Grid2
                size={{ xs: 12 }}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#011D33",
                    color: "#fff",
                    borderRadius: "12px", // Consistent rounded styling
                    "&:hover": { backgroundColor: "#0a3b63" },
                  }}
                  onClick={() => removeSection(index)}
                >
                  Remove Section
                </Button>
              </Grid2>
            </Grid2>
          ))}
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Button
              variant="contained"
              color="primary"
              sx={{
                width: { xs: "100%", md: "25%" },
                borderRadius: "12px",
                textTransform: "none",
                marginBottom: "15px",
                fontSize: "16px",
              }}
              onClick={addSection}
            >
              Add Section
            </Button>
          </Grid2>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Button
              type="submit"
              variant="contained"
              color="success"
              sx={{
                width: { xs: "100%", md: "25%" },
                borderRadius: "12px",
                textTransform: "none",
                position: "relative",
                fontSize: "16px",
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Submit"
              )}
            </Button>
          </Grid2>
        </form>
      </Container>
      <Footer />
    </>
  );
};

export default TermsAndConditionsForm;
